import React from 'react';
import { ChevronDown, Loader2 } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { publicAPI } from '../services/api';
import useAPI from '../hooks/useAPI';
import SEO, { generateFAQSchema } from '../components/SEO';

const FAQs = () => {
  const { data: faqs, loading, error } = useAPI(() => publicAPI.getFAQs(), []);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading FAQs...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <SEO 
        title="Frequently Asked Questions"
        description="Find answers to common questions about motorhome hire from Millie's Motorhome Hire. Learn about bookings, insurance, what's included, and more."
        keywords="motorhome hire FAQ, campervan rental questions, motorhome booking, insurance questions, driving licence requirements"
        url="/faqs"
        structuredData={faqs ? generateFAQSchema(faqs) : null}
      />
      {/* Hero Section */}
      <section className="bg-slate-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-6">Frequently Asked Questions</h1>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto">
            Find answers to common questions about hiring a motorhome from Millie's
          </p>
        </div>
      </section>

      {/* FAQs Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4">
          <Card className="shadow-lg">
            <CardContent className="p-8">
              <Accordion type="single" collapsible className="space-y-4">
                {(faqs || []).map((faq, index) => (
                  <AccordionItem key={faq.id || index} value={`item-${faq.id || index}`} className="border rounded-lg px-6 py-2">
                    <AccordionTrigger className="text-left hover:no-underline">
                      <span className="font-semibold text-slate-900">{faq.question}</span>
                    </AccordionTrigger>
                    <AccordionContent className="text-slate-700 leading-relaxed pt-2">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>

          {/* Still have questions */}
          <div className="mt-12 text-center">
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="p-8">
                <h2 className="text-2xl font-bold text-slate-900 mb-4">
                  Still have questions?
                </h2>
                <p className="text-slate-700 mb-6">
                  Can't find the answer you're looking for? Our friendly team is here to help.
                </p>
                <div className="flex flex-wrap gap-4 justify-center">
                  <Button size="lg" asChild>
                    <a href="mailto:milliesmotorhomehire@gmail.com">Email Us</a>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <a href="tel:07527006966">Call Us: 07527 006966</a>
                  </Button>
                  <Button size="lg" variant="outline" asChild>
                    <a 
                      href="https://wa.me/447527006966" 
                      target="_blank" 
                      rel="noopener noreferrer"
                    >
                      WhatsApp Us
                    </a>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Helpful Resources */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Helpful Resources</h2>
            <p className="text-slate-600">Learn more about motorhome hire and what to expect</p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">How It Works</h3>
                <p className="text-slate-600 mb-4">
                  Learn about our simple three-step hire process
                </p>
                <Button variant="outline" asChild>
                  <a href="/how-it-works">Learn More</a>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="bg-green-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">Our Fleet</h3>
                <p className="text-slate-600 mb-4">
                  Browse our range of 4 and 6 berth motorhomes
                </p>
                <Button variant="outline" asChild>
                  <a href="/motorhomes">View Motorhomes</a>
                </Button>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardContent className="p-6 text-center">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <svg className="h-8 w-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold mb-3">On The Day</h3>
                <p className="text-slate-600 mb-4">
                  What to expect on the day of your hire
                </p>
                <Button variant="outline" asChild>
                  <a href="/on-the-day">Read Guide</a>
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQs;
